import {ChangeDetectionStrategy, Component, computed, Injectable, input, signal, Signal} from "@angular/core";
import {AgendaelementId, AvstemningMedResultatModell, AvstemningsstatusModell, MøtedeltakerModell, MøteId, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {combineLatest, map, Observable, switchMap} from "rxjs";
import {toObservable, toSignal} from "@angular/core/rxjs-interop";
import {MøteDatatjeneste, MøteModellDLM} from "felles-ng2/tjenester/datalager/MøteDatatjeneste";
import {MøtedeltakereDatatjeneste} from "felles-ng2/tjenester/datalager/MøtedeltakereDatatjeneste";
import {AgendaelementModellDLM, SakDatatjeneste} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {AvstemningDatatjeneste} from "felles-ng2/tjenester/datalager/AvstemningDatatjeneste";
import {AvstemningsstatusDatatjeneste} from "felles-ng2/tjenester/datalager/AvstemningsstatusDatatjeneste";
import {tomAvstemningsstatusModell, tomMøteModell} from "felles-ng2/modell/tommeModeller";
import {TranslateModule} from "@ngx-translate/core";
import {C3TodeltPaiKomponent, C3TodeltPaiKonfig} from "web-ng2/delt/komponenter/c3-todelt-pai/c3-todelt-pai";
import {MatButton, MatIconButton} from "@angular/material/button";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {Dialog} from "felles-ng2/tjenester/Dialog";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {erTomListe} from "felles-ng2/støttefunksjoner/lister";
import {SakListeAvstemningerKomponent} from "web-ng2/moduler/sak/komponenter/sak-liste-avstemninger/sak-liste-avstemninger";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {TittelHandlingerHøyrestiltKomponent, TittelKomponent, TittelVerdiKomponent, VerdiKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {PercentPipe} from "@angular/common";
import {farge_grå_100_ørneflag, farge_suksess_200_hoved} from "felles-ng2/fargepalett";
import {MatIcon} from "@angular/material/icon";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {MatTooltip} from "@angular/material/tooltip";
import {AvstemningStemmeberettigedeKomponent} from "web-ng2/moduler/avstemning/komponenter/avstemning-stemmeberettigede/avstemning-stemmeberettigede";
import {MatDivider} from "@angular/material/list";
import {MatDialog} from "@angular/material/dialog";
import {ReactiveFormsModule} from "@angular/forms";
import {ÅpneStengeForAvstemningDialogKomponent} from "web-ng2/moduler/avstemning/modale/åpne-stenge-for-avstemning-dialog/åpne-stenge-for-avstemning-dialog";
import {stemmeberettigedeMedlemmer} from "felles-ng2/modell/møtedeltakerModellHjelpere";
import {dialogRespons} from "felles-ng2/infrastruktur/material/matDialogHjelpere";
import {AjsRouterLinkDirektiv} from "web-ng2/delt/direktiver/ajsRouterLink/ajsRouterLink";

const debug = initDebugger('AvstemningerListeKomponent');

/**
 * Viser alle avstemninger for et møte, gruppert per sak.
 * Merk at vi forventer at møte, deltakere og saker er lastet av kaller og tilgjengelig i datalager.
 */
@Component({
    selector: 'avstemninger-liste',
    templateUrl: 'avstemninger-liste.html',
    styleUrl: 'avstemninger-liste.scss',
    imports: [
        TranslateModule,
        C3TodeltPaiKomponent,
        MatButton,
        SakListeAvstemningerKomponent,
        TittelVerdiKomponent,
        VerdiKomponent,
        TipsKomponent,
        TittelKomponent,
        MatSlideToggle,
        PercentPipe,
        MatIcon,
        MatIconButton,
        MatMenu,
        MatMenuItem,
        MatMenuTrigger,
        MatTooltip,
        AvstemningStemmeberettigedeKomponent,
        TittelHandlingerHøyrestiltKomponent,
        MatDivider,
        ReactiveFormsModule,
        AjsRouterLinkDirektiv
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AvstemningerListeKomponent {
    static ajsSelector = 'avstemningerListe';

    moeteId = input.required<MøteId>();

    moete: Signal<MøteModellDLM>;
    moetedeltakere: Signal<MøtedeltakerModell[]>
    avstemningsstatus: Signal<AvstemningsstatusModell>;
    saker: Signal<AgendaelementModellDLM[]>;
    sakerOgAvstemninger: Signal<SakOgAvstemninger[]>;
    viserRedigerStemmeberettigede = signal(false);
    visAlleSaker = signal(false);

    harStemmeberettigede = computed(() => this.moetedeltakere().some(d => d.erStemmeberettiget));
    harAvstemninger = computed(() => this.avstemningsstatus().antallAvstemninger > 0);
    antallSakerUtenAvstemninger = computed(() => this.sakerOgAvstemninger().filter(innslag => erTomListe(innslag.avstemninger)).length);
    /** Sann dersom møtet ikke er låst for redigering og bruker har redigering-rolle i aktuell gruppe */
    brukerKanRedigere = computed(() => !this.moete().laastForRedigering && this.kontekst.autentisertBruker().inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, this.moete().eiendeGruppeId));

    produktAvStemmeberettigedeOgAvstemninger = computed(() => this.avstemningsstatus().antallStemmeberettigedeDeltakere * this.avstemningsstatus().antallAvstemninger);
    antallStemmerAvgitt = computed(() => Object.values(this.avstemningsstatus().antallStemmerAvgittPerAvstemning).reduce((sum, antallAvgitt) => sum + antallAvgitt, 0));

    constructor(private avhengigheter: AvstemningerListeKomponentAvhengigheter,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private oversettelser: Oversettelser,
                private dialog: Dialog,
                private matDialog: MatDialog,
                private kontekst: BrukerkontekstTilbyder) {
        const møteIdObserverbar = toObservable(this.moeteId);

        this.moete = toSignal(møteIdObserverbar.pipe(switchMap(id => this.avhengigheter.hentMøte(id)),), {initialValue: tomMøteModell()});
        this.moetedeltakere = toSignal(møteIdObserverbar.pipe(switchMap(id => this.avhengigheter.hentMøtedeltakere(id))), {initialValue: []})
        this.avstemningsstatus = toSignal(møteIdObserverbar.pipe(switchMap(id => this.avhengigheter.hentAvstemningsstatus(id))), {initialValue: tomAvstemningsstatusModell()});
        this.saker = toSignal(møteIdObserverbar.pipe(switchMap(id => this.avhengigheter.hentSaker(id))), {initialValue: []});

        this.sakerOgAvstemninger = toSignal(toObservable(this.saker).pipe(
          switchMap(saker => combineLatest(saker.map(sak =>
            this.avhengigheter.hentAvstemninger(sak.id).pipe(map(avstemninger => ({sak, avstemninger}))))))
        ), {initialValue: []});
    }

    stemmegivningDonutTooltip(): string {
        const tooltip: string[] = [];
        const totalt = this.avstemningsstatus().antallAvstemninger;
        if (this.avstemningsstatus().antallAvstemningerStemtIngen > 0)
            tooltip.push(this.oversettelser.oversett(i18n('{antall} av {totalt} avstemninger er ikke besvart'), {antall: this.avstemningsstatus().antallAvstemningerStemtIngen, totalt}));
        if (this.avstemningsstatus().antallAvstemningerStemtDelvis > 0)
            tooltip.push(this.oversettelser.oversett(i18n('{antall} av {totalt} avstemninger er delvis besvart'), {antall: this.avstemningsstatus().antallAvstemningerStemtDelvis, totalt}));
        if (this.avstemningsstatus().antallAvstemningerStemtFerdig > 0)
            tooltip.push(this.oversettelser.oversett(i18n('{antall} av {totalt} avstemninger er fullstendig besvart'), {antall: this.avstemningsstatus().antallAvstemningerStemtFerdig, totalt}));
        return tooltip.join(', ');
    }

    deltakereDonutKonfig(): Partial<C3TodeltPaiKonfig> {
        return {
            andelFarge: farge_suksess_200_hoved,
            ikkePåbegyntFarge: '#db7b7b', //@todo??
            restFarge: farge_grå_100_ørneflag,
            strektykkelse: 2
        };
    }

    redigerStemmeberettigede() {
        this.viserRedigerStemmeberettigede.set(true);
    }

    async aapneForAvstemning() {
        const ok = await dialogRespons(ÅpneStengeForAvstemningDialogKomponent.visÅpne(this.matDialog, this.moeteId(), this.moete().eiendeGruppeId, stemmeberettigedeMedlemmer(this.moetedeltakere()), this.avstemningsstatus()));
        if (ok) this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Møtet er nå åpent for avstemning')));
    }

    async stengForAvstemning() {
        const ok = await dialogRespons(ÅpneStengeForAvstemningDialogKomponent.visStenge(this.matDialog, this.moeteId(), this.moete().eiendeGruppeId, stemmeberettigedeMedlemmer(this.moetedeltakere()), this.avstemningsstatus()));
        if (ok) this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Møtet er nå stengt for avstemning')));
    }

    async nullstillAvgitteStemmerMedBekreftelsesdialog() {
        if (!await this.dialog.fare({
            tittel: this.oversettelser.oversett(i18n('Nullstill alle stemmer')),
            innhold: this.oversettelser.oversett(i18n('Vil du slette alle avgitte stemmer knyttet til dette møtet?')),
            okKnapp: this.oversettelser.oversett(i18n('Slett alle avgitte stemmer')),
            avbrytKnapp: this.oversettelser.oversett(i18n('Avbryt'))
        })) return;

        await this.avhengigheter.nullstillAvgitteStemmer(this.moeteId());
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Avgitte stemmer har blitt nullstilt')));
    }

}

type SakOgAvstemninger = { sak: AgendaelementModellDLM, avstemninger: AvstemningMedResultatModell[] }

@Injectable()
export class AvstemningerListeKomponentAvhengigheter {
    constructor(
      private møteDatatjeneste: MøteDatatjeneste,
      private møtedeltakereDatatjeneste: MøtedeltakereDatatjeneste,
      private sakDatatjeneste: SakDatatjeneste,
      private avstemningDatatjeneste: AvstemningDatatjeneste,
      private avstemningsstatusDatatjeneste: AvstemningsstatusDatatjeneste) {
    }

    hentMøte(id: MøteId): Observable<MøteModellDLM> {
        return this.møteDatatjeneste.hentFraFraLokallagerSomObservable(id);
    }

    hentMøtedeltakere(møteId: MøteId): Observable<MøtedeltakerModell[]> {
        return this.møtedeltakereDatatjeneste.finnTilknyttetMøteFraLokallager(møteId);
    }

    hentAvstemningsstatus(id: MøteId): Observable<AvstemningsstatusModell> {
        return this.avstemningsstatusDatatjeneste.hent(id);
    }

    hentSaker(møteId: MøteId): Observable<AgendaelementModellDLM[]> {
        return this.sakDatatjeneste.finnTilknyttetMøteFraLokallager(møteId)
    }

    hentAvstemninger(sakId: AgendaelementId): Observable<AvstemningMedResultatModell[]> {
        return this.avstemningDatatjeneste.finnTilknyttetSakFraLokallager(sakId);
    }

    async nullstillAvgitteStemmer(møteId: MøteId): Promise<void> {
        await this.avstemningDatatjeneste.nullstillAlleAvgitteStemmer(møteId);
    }


}
